import type { DocumentFieldState, StructuredDocumentRecord } from '@/features/case-workspace/types'
import {
  type HtsRegulatoryFlag,
  type NormalizedHtsRecord,
  type UsitcHtsLookupService,
  usitcHtsLookupService,
} from '@/features/case-workspace/usitcHts'
import { deriveCbp7501LineAutomation } from '@/features/case-workspace/cbp7501LineAutomation'

export type Cbp7501FieldMode = 'required' | 'optional' | 'disabled' | 'warning' | 'error'

export type Cbp7501DynamicFieldState = {
  mode: Cbp7501FieldMode
  disabled: boolean
  required: boolean
  showWarningWhenEmpty: boolean
  reason: string
  flags: HtsRegulatoryFlag[]
  overrideEnabled: boolean
  excludedFromSubmission: boolean
}

export type Cbp7501LineRegulatoryContext = {
  lineItemId: string
  htsCode: string
  lookupRecord: NormalizedHtsRecord | null
  flags: HtsRegulatoryFlag[]
}

export type Cbp7501FieldStateEngineResult = {
  datasetManifest: ReturnType<UsitcHtsLookupService['getDatasetManifest']>
  aggregateFlags: HtsRegulatoryFlag[]
  lineContexts: Cbp7501LineRegulatoryContext[]
  topLevelFields: Record<string, Cbp7501DynamicFieldState>
  lineItemFields: Record<string, Record<string, Cbp7501DynamicFieldState>>
}

const ENTRY_SUMMARY_ENTRY_TYPE_CODES = new Set(['01', '11'])

const TOP_LEVEL_DYNAMIC_FIELD_KEYS = [
  'country_of_melt_and_pour',
  'primary_country_of_smelt',
  'secondary_country_of_smelt',
  'country_of_cast',
] as const

const LINE_DYNAMIC_FIELD_KEYS = [
  'ad_cvd_number',
  'ad_cvd_rate',
  'charges',
  'irc_rate',
  'manifest_quantity',
  'manifest_quantity_unit',
  'visa_certificate_number',
] as const

export function requiresManifestQuantityForRecord(record: StructuredDocumentRecord) {
  const entryType = record.fields.entry_type?.value?.trim() ?? ''
  const hasTransportQuantityLink = Boolean(record.fields.bill_of_lading_awb_number?.value?.trim())
  return hasTransportQuantityLink || ENTRY_SUMMARY_ENTRY_TYPE_CODES.has(entryType)
}

export function requiresChargesForRecord(record: StructuredDocumentRecord) {
  const modeOfTransport = record.fields.mode_of_transport?.value?.trim() ?? ''
  return modeOfTransport !== '60'
}

export function isManifestQuantityEnabledForLine(
  record: StructuredDocumentRecord,
  lineItem: StructuredDocumentRecord['lineItems'][number],
) {
  const storedPreference = lineItem.fields.manifest_quantity_available?.value?.trim()
  if (storedPreference === 'true') return true
  if (storedPreference === 'false') return false

  return (
    requiresManifestQuantityForRecord(record)
    || Boolean(lineItem.fields.manifest_quantity?.value?.trim())
    || Boolean(lineItem.fields.manifest_quantity_unit?.value?.trim())
  )
}

function optionalState(reason: string, fieldState?: DocumentFieldState, flags: HtsRegulatoryFlag[] = []): Cbp7501DynamicFieldState {
  const overrideEnabled = Boolean(fieldState?.overrideEnabled)
  return {
    mode: 'optional',
    disabled: false,
    required: false,
    showWarningWhenEmpty: false,
    reason: overrideEnabled ? `${reason} Override is enabled for manual review.` : reason,
    flags,
    overrideEnabled,
    excludedFromSubmission: false,
  }
}

function disabledState(reason: string, fieldState?: DocumentFieldState, flags: HtsRegulatoryFlag[] = []): Cbp7501DynamicFieldState {
  const overrideEnabled = Boolean(fieldState?.overrideEnabled)
  return {
    mode: overrideEnabled ? 'optional' : 'disabled',
    disabled: !overrideEnabled,
    required: false,
    showWarningWhenEmpty: false,
    reason: overrideEnabled ? `Override enabled. ${fieldState?.overrideReason ?? 'Reason not recorded.'}` : reason,
    flags,
    overrideEnabled,
    excludedFromSubmission: !overrideEnabled,
  }
}

function requiredState(reason: string, fieldState?: DocumentFieldState, flags: HtsRegulatoryFlag[] = []): Cbp7501DynamicFieldState {
  const overrideEnabled = Boolean(fieldState?.overrideEnabled)
  return {
    mode: 'required',
    disabled: false,
    required: true,
    showWarningWhenEmpty: false,
    reason: overrideEnabled ? `${reason} Override is enabled for manual review.` : reason,
    flags,
    overrideEnabled,
    excludedFromSubmission: false,
  }
}

function warningState(reason: string, fieldState?: DocumentFieldState, flags: HtsRegulatoryFlag[] = []): Cbp7501DynamicFieldState {
  const overrideEnabled = Boolean(fieldState?.overrideEnabled)
  return {
    mode: 'warning',
    disabled: false,
    required: false,
    showWarningWhenEmpty: true,
    reason: overrideEnabled ? `${reason} Override is enabled for manual review.` : reason,
    flags,
    overrideEnabled,
    excludedFromSubmission: false,
  }
}

function uniqueFlags(flags: HtsRegulatoryFlag[]) {
  return [...new Set(flags)]
}

function lineFlags(record: StructuredDocumentRecord, lookupService: UsitcHtsLookupService) {
  return (record.lineItems ?? []).map((lineItem) => {
    const htsCode = lineItem.fields.htsus_number?.value?.trim() ?? ''
    const lookupRecord = lookupService.lookup(htsCode)
    return {
      lineItemId: lineItem.id,
      htsCode,
      lookupRecord,
      flags: uniqueFlags(lookupRecord?.flags ?? []),
    }
  })
}

export function buildCbp7501FieldStateEngine(
  record: StructuredDocumentRecord,
  lookupService: UsitcHtsLookupService = usitcHtsLookupService,
): Cbp7501FieldStateEngineResult | null {
  if (record.documentType !== 'cbp_7501') return null

  const lineContexts = lineFlags(record, lookupService)
  const aggregateFlags = uniqueFlags(lineContexts.flatMap((line) => line.flags))
  const manifestQuantityRequired = requiresManifestQuantityForRecord(record)
  const chargesRequired = requiresChargesForRecord(record)

  const topLevelFields = Object.fromEntries(
    TOP_LEVEL_DYNAMIC_FIELD_KEYS.map((fieldKey) => [
      fieldKey,
      disabledState('This field is not required for the current HTS-driven import scenario.', record.fields[fieldKey]),
    ]),
  ) as Record<string, Cbp7501DynamicFieldState>

  if (aggregateFlags.includes('SECTION_232_STEEL')) {
    topLevelFields.country_of_melt_and_pour = requiredState(
      'Required because at least one HTS line is flagged for Section 232 steel reporting.',
      record.fields.country_of_melt_and_pour,
      ['SECTION_232_STEEL'],
    )
  }

  if (aggregateFlags.includes('SECTION_232_ALUMINUM')) {
    topLevelFields.primary_country_of_smelt = requiredState(
      'Required because at least one HTS line is flagged for Section 232 aluminum reporting.',
      record.fields.primary_country_of_smelt,
      ['SECTION_232_ALUMINUM'],
    )
    topLevelFields.secondary_country_of_smelt = requiredState(
      'Required because at least one HTS line is flagged for Section 232 aluminum reporting.',
      record.fields.secondary_country_of_smelt,
      ['SECTION_232_ALUMINUM'],
    )
    topLevelFields.country_of_cast = requiredState(
      'Required because at least one HTS line is flagged for Section 232 aluminum reporting.',
      record.fields.country_of_cast,
      ['SECTION_232_ALUMINUM'],
    )
  }

  const lineItemFields = Object.fromEntries(
    lineContexts.map((lineContext) => {
      const lineItem = record.lineItems.find((candidate) => candidate.id === lineContext.lineItemId)
      const fieldStates = Object.fromEntries(
        LINE_DYNAMIC_FIELD_KEYS.map((fieldKey) => [
          fieldKey,
          disabledState(
            'This field is not required for the HTS code on this line item.',
            lineItem?.fields[fieldKey],
          ),
        ]),
      ) as Record<string, Cbp7501DynamicFieldState>

      if (lineContext.flags.includes('AD_CVD_POSSIBLE')) {
        fieldStates.ad_cvd_number = warningState(
          'This HTS line may require AD/CVD review. Complete the AD/CVD case number if applicable.',
          lineItem?.fields.ad_cvd_number,
          ['AD_CVD_POSSIBLE'],
        )
        fieldStates.ad_cvd_rate = warningState(
          'This HTS line may require AD/CVD review. Complete the AD/CVD rate if applicable.',
          lineItem?.fields.ad_cvd_rate,
          ['AD_CVD_POSSIBLE'],
        )
      }

      if (lineContext.flags.includes('QUOTA_OR_VISA')) {
        fieldStates.visa_certificate_number = warningState(
          'This HTS line is flagged for quota or visa review. Complete the visa or certificate field when the program requires it.',
          lineItem?.fields.visa_certificate_number,
          ['QUOTA_OR_VISA'],
        )
      }

      const automation = lineItem ? deriveCbp7501LineAutomation(lineItem) : null

      if (
        lineContext.flags.includes('IRC_TAX_APPLICABLE')
        && automation?.ircRate
        && automation.ircRate !== 'Not Applicable to HTSUS code'
      ) {
        fieldStates.irc_rate = warningState(
          'This HTS line is flagged for Internal Revenue Code tax review.',
          lineItem?.fields.irc_rate,
          ['IRC_TAX_APPLICABLE'],
        )
      }

      if (chargesRequired) {
        fieldStates.charges = requiredState(
          'Charges are required unless Mode of Transport is 60 (passenger, hand-carried merchandise).',
          lineItem?.fields.charges,
        )
      } else {
        fieldStates.charges = disabledState(
          'Charges are not required for passenger, hand-carried merchandise.',
          lineItem?.fields.charges,
        )
      }

      const manifestQuantityEnabled = Boolean(lineItem && isManifestQuantityEnabledForLine(record, lineItem))

      if (!manifestQuantityEnabled) {
        fieldStates.manifest_quantity = disabledState(
          'Manifest Quantity is only required when the entry summary serves as entry/entry summary and should match the quantity reported on the B/L or AWB.',
          lineItem?.fields.manifest_quantity,
        )
        fieldStates.manifest_quantity_unit = disabledState(
          'Manifest Quantity is only required when the entry summary serves as entry/entry summary and should match the quantity reported on the B/L or AWB.',
          lineItem?.fields.manifest_quantity_unit,
        )
      } else if (manifestQuantityRequired) {
        fieldStates.manifest_quantity = requiredState(
          'Manifest Quantity is required because this filing scenario is serving as the entry/entry summary or is linked to a B/L or AWB quantity.',
          lineItem?.fields.manifest_quantity,
        )
        fieldStates.manifest_quantity_unit = requiredState(
          'Manifest Quantity unit is required when Manifest Quantity applies for this filing scenario.',
          lineItem?.fields.manifest_quantity_unit,
        )
      } else {
        fieldStates.manifest_quantity = optionalState(
          'Manifest Quantity is available for manual entry when supporting manifest data is available.',
          lineItem?.fields.manifest_quantity,
        )
        fieldStates.manifest_quantity_unit = optionalState(
          'Manifest Quantity unit is available for manual entry when supporting manifest data is available.',
          lineItem?.fields.manifest_quantity_unit,
        )
      }

      return [lineContext.lineItemId, fieldStates]
    }),
  )

  return {
    datasetManifest: lookupService.getDatasetManifest(),
    aggregateFlags,
    lineContexts,
    topLevelFields,
    lineItemFields,
  }
}

export function sanitizeCbp7501ForSubmission(
  record: StructuredDocumentRecord,
  lookupService: UsitcHtsLookupService = usitcHtsLookupService,
) {
  const engine = buildCbp7501FieldStateEngine(record, lookupService)
  if (!engine) return record

  return {
    ...record,
    fields: Object.fromEntries(
      Object.entries(record.fields).map(([fieldKey, fieldState]) => {
        const runtimeState = engine.topLevelFields[fieldKey]
        if (!runtimeState?.excludedFromSubmission) return [fieldKey, fieldState]
        return [
          fieldKey,
          {
            ...fieldState,
            value: '',
            displayValue: '',
          },
        ]
      }),
    ),
    lineItems: record.lineItems.map((lineItem) => ({
      ...lineItem,
      fields: Object.fromEntries(
        Object.entries(lineItem.fields).map(([fieldKey, fieldState]) => {
          const runtimeState = engine.lineItemFields[lineItem.id]?.[fieldKey]
          if (!runtimeState?.excludedFromSubmission) return [fieldKey, fieldState]
          return [
            fieldKey,
            {
              ...fieldState,
              value: '',
              displayValue: '',
            },
          ]
        }),
      ),
    })),
  }
}
